import React, { useState, useEffect } from "react";
import { useRoute, Link } from "wouter";
import {
  useGetAgent,
  useGetMe,
  useUpdateAgent,
  useListCommissionPlans,
  useSetAgentCommissionPlan,
  useListBankChangeRequests,
  useListOrders,
  useListTransactions,
  useListTickets,
  useListDisputes,
  useListAuditLogs,
  useGetAgentCompliance,
  useGetAgentTraining,
  useGetAgentNotifications,
  useGetOnboardingMsa,
  useSendOnboardingMsa,
  useListAreas,
  useListRegions,
  useListAccountManagers,
  useListMasterDealers,
  getGetAgentQueryKey,
  getListAgentsQueryKey,
  getListCommissionPlansQueryKey,
} from "@workspace/api-client-react";
import type { Agent } from "@workspace/api-client-react";
import type { AgentFicaDocument } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthenticatedDocumentLink } from "@/components/AuthenticatedDocumentLink";
import { MsaContractDocument } from "@/components/onboarding/MsaContractDocument";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  Ban,
  ShieldCheck,
  Loader2,
  Building2,
  UserCog,
  Compass,
  Clock,
  AlertTriangle,
  Send,
  Pencil,
} from "lucide-react";

const MANAGE_ROLES = ["master_dealer", "account_manager", "admin"];
// Commission-plan endpoints are server-gated to admin + account_manager only
// (see api-server routes/index.ts). Master dealers must NOT see this control.
const COMMISSION_MANAGE_ROLES = ["account_manager", "admin"];
// Bank-change data is server-gated to agent/finance/admin, but this profile page
// is only reachable by master_dealer/account_manager/admin — so the only role
// that can BOTH open this page and read bank-change requests is admin.
const BANK_VIEW_ROLES = ["admin"];
// Compliance (KYC + MSA) carries sensitive onboarding PII; the server gates
// GET /agents/:id/compliance to admin + account_manager, mirrored here for UX.
const COMPLIANCE_VIEW_ROLES = ["account_manager", "admin"];
// Training / LMS readiness is reviewer-only; server gates GET /agents/:id/training
// to admin + account_manager, mirrored here for UX.
const TRAINING_VIEW_ROLES = ["account_manager", "admin"];
// Assignment edits (area / master dealer / account manager) are admin +
// account_manager only — master_dealer keeps a read-only profile view.
const EDIT_ASSIGN_ROLES = ["account_manager", "admin"];
// The agent (OpenServe) code may ALSO be edited by operations + poc_support — but
// NOT master_dealer. Those two get a code-ONLY dialog (no assignment/activation).
// Mirrors the server PATCH /agents code-edit gate (admin/AM/operations/poc_support).
const CODE_EDIT_ROLES = [
  "account_manager",
  "admin",
  "operations",
  "poc_support",
];

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  cheque: "Cheque / Current",
  savings: "Savings",
};

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  payout: "Payout",
  once_off_connection_fee: "Once-off connection fee",
  recurring_commission: "Recurring commission",
  basic_retainer: "Basic retainer",
};

function formatCents(cents?: number) {
  const v = (cents ?? 0) / 100;
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(v);
}

function formatDate(iso?: string | null) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso?: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AgentProfile() {
  const [, params] = useRoute("/agents/:id");
  const id = Number(params?.id);
  const { data: agent, isLoading, error } = useGetAgent(id);
  const { data: me } = useGetMe();
  const canManage = MANAGE_ROLES.includes(me?.role ?? "");
  const canManageCommission = COMMISSION_MANAGE_ROLES.includes(me?.role ?? "");
  const canViewBank = BANK_VIEW_ROLES.includes(me?.role ?? "");
  const canViewCompliance = COMPLIANCE_VIEW_ROLES.includes(me?.role ?? "");
  const canViewTraining = TRAINING_VIEW_ROLES.includes(me?.role ?? "");
  const canEditAssignment = EDIT_ASSIGN_ROLES.includes(me?.role ?? "");
  const canEditCode = CODE_EDIT_ROLES.includes(me?.role ?? "");
  const [editAssignmentOpen, setEditAssignmentOpen] = useState(false);
  const [editCodeOpen, setEditCodeOpen] = useState(false);
  // Activity is visible to every role that can open this page; the individual
  // sections inside ActivityTab are gated per-role to match each endpoint.
  const canViewActivity = MANAGE_ROLES.includes(me?.role ?? "");

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !agent)
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="p-6 text-red-500">Failed to load this agent.</div>
      </div>
    );

  const hasDocuments = Boolean(
    agent.onboardingId &&
      agent.ficaDocuments &&
      agent.ficaDocuments.length > 0,
  );
  // Inactivity flag: an agent with no confirmed lead in INACTIVE_THRESHOLD_DAYS
  // is surfaced in amber on the "Last lead" row.
  const INACTIVE_THRESHOLD_DAYS = 14;
  const days = agent.daysSinceLastLead ?? null;
  const isInactive = days != null && days >= INACTIVE_THRESHOLD_DAYS;
  const lastLeadValue = agent.lastLeadDate
    ? `${formatDate(agent.lastLeadDate)}${days != null ? ` · ${days}d ago` : ""}`
    : "No leads yet";
  const lastLoginValue = formatDateTime(agent.lastLoginAt) ?? "Never signed in";
  // heldPayoutCount is only present on the wire for admin/finance; absent for
  // other roles, so the banner naturally stays hidden for them.
  const heldCount = agent.heldPayoutCount ?? 0;
  const tabs: { value: string; label: string }[] = [
    { value: "overview", label: "Overview" },
  ];
  if (canViewCompliance)
    tabs.push({ value: "compliance", label: "Compliance" });
  // The MSA agreement applies to field-sales agents only.
  if (canViewCompliance && agent.onboardingId && agent.type === "field_sales")
    tabs.push({ value: "agreement", label: "Agreement" });
  if (canViewTraining) tabs.push({ value: "training", label: "Training" });
  if (canManageCommission)
    tabs.push({ value: "commission", label: "Commission" });
  if (canViewBank) tabs.push({ value: "bank", label: "Bank" });
  if (canViewActivity) tabs.push({ value: "activity", label: "Activity" });
  if (hasDocuments) tabs.push({ value: "documents", label: "Documents" });

  return (
    <div className="space-y-6">
      <BackLink />

      {heldCount > 0 ? (
        <div className="flex items-start gap-3 rounded-lg border border-[#F59E0B]/40 bg-[#FFF7ED] p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#B45309]" />
          <div>
            <p className="text-sm font-semibold text-[#B45309]">
              {heldCount} payout{heldCount === 1 ? "" : "s"} on hold
            </p>
            <p className="text-sm text-[#92400E]">
              {formatCents(agent.heldPayoutCents)} is being withheld pending
              review.
            </p>
          </div>
        </div>
      ) : null}

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 border border-gray-100">
                {agent.profilePhotoUrl ? (
                  <AvatarImage
                    src={`/api/storage${agent.profilePhotoUrl}`}
                    alt={agent.name}
                  />
                ) : null}
                <AvatarFallback className="bg-gray-100 text-[#0B132B] text-lg font-semibold">
                  {agent.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-2xl font-bold text-[#0B132B]">
                  {agent.name}
                </h1>
                <p className="text-sm text-gray-500 capitalize">
                  {agent.type.replace(/_/g, " ")} ·{" "}
                  {agent.openserveDealerCode ?? agent.dealerCode ?? "—"}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-start gap-3 sm:items-end">
              <StatusBadge status={agent.status} />
              {canManage ? (
                <StatusActions
                  agentId={agent.id}
                  status={agent.status}
                  agentName={agent.name}
                  canSetActive={canEditAssignment}
                />
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="w-full">
        {tabs.length > 1 ? (
          <TabsList>
            {tabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        ) : null}

        <TabsContent value="overview" className="mt-4 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Orders submitted"
              value={String(agent.ordersSubmitted ?? 0)}
            />
            <StatCard
              label="Active clients"
              value={String(agent.activeClients ?? 0)}
            />
            <StatCard
              label="Conversion rate"
              value={`${Math.round((agent.conversionRate ?? 0) * 100)}%`}
            />
            <StatCard
              label="Wallet balance"
              value={formatCents(agent.walletBalanceCents)}
              accent="#00A859"
            />
          </div>

          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg text-[#0B132B]">Details</CardTitle>
              {canEditAssignment ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditAssignmentOpen(true)}
                >
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit assignment
                </Button>
              ) : canEditCode ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditCodeOpen(true)}
                >
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit code
                </Button>
              ) : null}
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailRow icon={Mail} label="Email" value={agent.email || "-"} />
              <DetailRow icon={Phone} label="Phone" value={agent.phone || "-"} />
              <DetailRow
                icon={ShieldCheck}
                label="Mobile OTP verification"
                value={agent.phoneVerified ? "Verified" : "Not verified"}
                tone={agent.phoneVerified ? "default" : "warning"}
              />
              <DetailRow
                icon={FileText}
                label="OpenServe dealer code"
                value={agent.openserveDealerCode ?? agent.dealerCode ?? "—"}
                tone={
                  (agent.openserveDealerCode ?? agent.dealerCode)
                    ? "default"
                    : "warning"
                }
              />
              <DetailRow
                icon={MapPin}
                label="Area"
                value={agent.areaName || "-"}
              />
              <DetailRow
                icon={Compass}
                label="Preferred operating area"
                value={agent.preferredOperatingArea || "-"}
              />
              <DetailRow
                icon={Building2}
                label="Master dealer"
                value={agent.masterDealerName || "-"}
              />
              <DetailRow
                icon={UserCog}
                label="Account manager"
                value={agent.accountManagerName || "-"}
              />
              <DetailRow
                icon={Calendar}
                label="Last lead"
                value={lastLeadValue}
                tone={isInactive ? "warning" : "default"}
              />
              <DetailRow
                icon={Clock}
                label="Last login"
                value={lastLoginValue}
              />
              <DetailRow
                icon={Calendar}
                label="Joined"
                value={formatDate(agent.createdAt)}
              />
            </CardContent>
          </Card>
          {canEditAssignment ? (
            <EditAssignmentDialog
              agent={agent}
              open={editAssignmentOpen}
              onOpenChange={setEditAssignmentOpen}
            />
          ) : canEditCode ? (
            <EditCodeDialog
              agent={agent}
              open={editCodeOpen}
              onOpenChange={setEditCodeOpen}
            />
          ) : null}
        </TabsContent>

        {canViewCompliance ? (
          <TabsContent value="compliance" className="mt-4">
            <ComplianceTab
              agentId={agent.id}
              onboardingId={agent.onboardingId}
              ficaDocuments={agent.ficaDocuments}
            />
          </TabsContent>
        ) : null}

        {canViewCompliance &&
        agent.onboardingId &&
        agent.type === "field_sales" ? (
          <TabsContent value="agreement" className="mt-4">
            <AgreementTab onboardingId={agent.onboardingId} />
          </TabsContent>
        ) : null}

        {canViewTraining ? (
          <TabsContent value="training" className="mt-4">
            <TrainingTab agentId={agent.id} />
          </TabsContent>
        ) : null}

        {canManageCommission ? (
          <TabsContent value="commission" className="mt-4">
            <CommissionPlanCard
              agentId={agent.id}
              currentPlanId={agent.commissionPlanId}
            />
          </TabsContent>
        ) : null}

        {canViewBank ? (
          <TabsContent value="bank" className="mt-4">
            <BankTab agentId={agent.id} />
          </TabsContent>
        ) : null}

        {canViewActivity ? (
          <TabsContent value="activity" className="mt-4">
            <ActivityTab agentId={agent.id} role={me?.role ?? ""} />
          </TabsContent>
        ) : null}

        {hasDocuments ? (
          <TabsContent value="documents" className="mt-4">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-[#0B132B]">
                  FICA documents
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {agent.ficaDocuments?.map((doc) => (
                  <AuthenticatedDocumentLink
                    key={doc.documentKey}
                    apiPath={`/api/onboarding-reviews/${agent.onboardingId}/documents/${doc.documentKey}`}
                    label={doc.label}
                    variant="card"
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}

// Pull a human-readable reason out of a failed mutation so the admin sees the
// server's specific message (e.g. which activation prerequisites are missing)
// instead of a generic "something went wrong". Defensive across error shapes.
function serverErrorMessage(err: unknown): string | null {
  if (typeof err !== "object" || err === null) return null;
  const data = (err as { response?: { data?: unknown } }).response?.data;
  if (typeof data === "object" && data !== null) {
    const { message, error } = data as { message?: unknown; error?: unknown };
    if (typeof message === "string" && message.trim()) return message;
    if (typeof error === "string" && error.trim()) return error;
  }
  return null;
}

function StatusActions({
  agentId,
  status,
  agentName,
  canSetActive,
}: {
  agentId: number;
  status: string;
  agentName: string;
  canSetActive: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateAgent = useUpdateAgent();
  const [pendingAction, setPendingAction] = useState<
    "suspend" | "reactivate" | "activate" | null
  >(null);

  const canReactivate = status === "suspended" && canSetActive;
  const canActivate = status === "pending" && canSetActive;
  const canSuspend = status === "active" || status === "pending";
  if (!canReactivate && !canSuspend) return null;

  const isSuspend = pendingAction === "suspend";
  const isActivate = pendingAction === "activate";

  async function runUpdate() {
    if (!pendingAction) return;
    const nextStatus = pendingAction === "suspend" ? "suspended" : "active";
    try {
      await updateAgent.mutateAsync({
        id: agentId,
        data: { status: nextStatus },
      });
      await queryClient.invalidateQueries({
        queryKey: getGetAgentQueryKey(agentId),
      });
      await queryClient.invalidateQueries({
        queryKey: getListAgentsQueryKey(),
      });
      toast({
        title:
          pendingAction === "suspend"
            ? "Agent suspended"
            : pendingAction === "activate"
              ? "Agent activated"
              : "Agent reactivated",
        description:
          pendingAction === "suspend"
            ? `${agentName} is now marked as suspended.`
            : `${agentName} is now marked as active.`,
      });
      setPendingAction(null);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Could not update agent",
        description:
          serverErrorMessage(err) ?? "Something went wrong. Please try again.",
      });
    }
  }

  return (
    <>
      {canReactivate ? (
        <Button
          className="bg-[#00A859] text-white hover:bg-[#00934d]"
          onClick={() => setPendingAction("reactivate")}
        >
          <ShieldCheck className="mr-2 h-4 w-4" /> Reactivate agent
        </Button>
      ) : (
        <div className="flex flex-wrap gap-2">
          {canActivate ? (
            <Button
              className="bg-[#00A859] text-white hover:bg-[#00934d]"
              onClick={() => setPendingAction("activate")}
            >
              <ShieldCheck className="mr-2 h-4 w-4" /> Activate agent
            </Button>
          ) : null}
          <Button
            variant="destructive"
            onClick={() => setPendingAction("suspend")}
          >
            <Ban className="mr-2 h-4 w-4" /> Suspend agent
          </Button>
        </div>
      )}

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open && !updateAgent.isPending) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isSuspend
                ? "Suspend this agent?"
                : isActivate
                  ? "Activate this agent?"
                  : "Reactivate this agent?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isSuspend
                ? `${agentName} will be marked as suspended. You can reactivate them at any time.`
                : isActivate
                  ? `${agentName} will be marked as active. This requires their area, master dealer, account manager, OpenServe dealer code, full name and an OTP-verified mobile — the change is rejected if any are missing.`
                  : `${agentName} will be marked as active again.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateAgent.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={updateAgent.isPending}
              onClick={(e) => {
                e.preventDefault();
                void runUpdate();
              }}
              className={
                isSuspend
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-[#00A859] text-white hover:bg-[#00934d]"
              }
            >
              {updateAgent.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {isSuspend
                ? "Suspend agent"
                : isActivate
                  ? "Activate agent"
                  : "Reactivate agent"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function EditAssignmentDialog({
  agent,
  open,
  onOpenChange,
}: {
  agent: Agent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateAgent = useUpdateAgent();
  const { data: areas } = useListAreas();
  const { data: regions } = useListRegions();
  const { data: accountManagers } = useListAccountManagers();
  const { data: dealersPage } = useListMasterDealers();
  const dealers = dealersPage?.items ?? [];

  const [areaId, setAreaId] = useState<number | null>(agent.areaId ?? null);
  const [masterDealerId, setMasterDealerId] = useState<number | null>(
    agent.masterDealerId ?? null,
  );
  const [accountManagerId, setAccountManagerId] = useState<number | null>(
    agent.accountManagerId ?? null,
  );
  const [openserveCode, setOpenserveCode] = useState(
    agent.openserveDealerCode ?? "",
  );
  const [confirmCode, setConfirmCode] = useState("");

  // Reset the form to the agent's current values each time the dialog opens.
  useEffect(() => {
    if (open) {
      setAreaId(agent.areaId ?? null);
      setMasterDealerId(agent.masterDealerId ?? null);
      setAccountManagerId(agent.accountManagerId ?? null);
      setOpenserveCode(agent.openserveDealerCode ?? "");
      setConfirmCode("");
    }
  }, [
    open,
    agent.areaId,
    agent.masterDealerId,
    agent.accountManagerId,
    agent.openserveDealerCode,
  ]);

  // Default the master dealer to Mooya Wireless when none is set yet.
  useEffect(() => {
    if (!open || masterDealerId != null) return;
    const mooya = dealers.find((d) => d.name === "Mooya Wireless");
    if (mooya) setMasterDealerId(mooya.id);
  }, [open, masterDealerId, dealers]);

  // Selecting an area auto-suggests the account manager that owns its region.
  function handleAreaChange(value: string) {
    const next = Number(value);
    setAreaId(next);
    const area = (areas ?? []).find((a) => a.id === next);
    const region = area
      ? (regions ?? []).find((r) => r.id === area.regionId)
      : undefined;
    if (region?.accountManagerId != null) {
      setAccountManagerId(region.accountManagerId);
    }
  }

  const trimmedCode = openserveCode.trim();
  const originalCode = (agent.openserveDealerCode ?? "").trim();
  const codeChanged = trimmedCode !== originalCode;
  // Double-entry: a NEW/changed non-empty code must be typed identically twice
  // before it can be saved (the OpenServe code is the agent's ONE code).
  const codeNeedsConfirm = codeChanged && trimmedCode.length > 0;
  const codeConfirmed = !codeNeedsConfirm || confirmCode.trim() === trimmedCode;
  const showCodeMismatch =
    codeNeedsConfirm &&
    confirmCode.trim().length > 0 &&
    confirmCode.trim() !== trimmedCode;
  // Only send the code when the reviewer actually entered a NEW non-empty value.
  // Assignment-only saves must NOT touch the code — otherwise a legacy agent
  // (openserveDealerCode null, the code still living in the dealerCode mirror)
  // would have its mirror wiped, and active agents would be rejected by the
  // server's active invariant. Code changes still go through the EditCode dialog.
  const includeCode = codeChanged && trimmedCode.length > 0;

  async function handleSave() {
    if (!codeConfirmed) return;
    try {
      await updateAgent.mutateAsync({
        id: agent.id,
        data: {
          ...(areaId != null ? { areaId } : {}),
          ...(masterDealerId != null ? { masterDealerId } : {}),
          ...(accountManagerId != null ? { accountManagerId } : {}),
          ...(includeCode ? { openserveDealerCode: trimmedCode } : {}),
        },
      });
      await queryClient.invalidateQueries({
        queryKey: getGetAgentQueryKey(agent.id),
      });
      await queryClient.invalidateQueries({
        queryKey: getListAgentsQueryKey(),
      });
      toast({
        title: "Assignment updated",
        description: `${agent.name}'s assignment has been saved.`,
      });
      onOpenChange(false);
    } catch {
      toast({
        variant: "destructive",
        title: "Could not update assignment",
        description: "Something went wrong. Please try again.",
      });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!updateAgent.isPending) onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit assignment</DialogTitle>
          <DialogDescription>
            Set the area, master dealer, account manager and OpenServe dealer
            code for {agent.name}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#0B132B]">Area</Label>
            <Select
              value={areaId != null ? String(areaId) : undefined}
              onValueChange={handleAreaChange}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select an area" />
              </SelectTrigger>
              <SelectContent>
                {(areas ?? []).map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#0B132B]">
              Master dealer
            </Label>
            <Select
              value={masterDealerId != null ? String(masterDealerId) : undefined}
              onValueChange={(v) => setMasterDealerId(Number(v))}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select a master dealer" />
              </SelectTrigger>
              <SelectContent>
                {dealers.map((d) => (
                  <SelectItem key={d.id} value={String(d.id)}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#0B132B]">
              Account manager
            </Label>
            <Select
              value={
                accountManagerId != null ? String(accountManagerId) : undefined
              }
              onValueChange={(v) => setAccountManagerId(Number(v))}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select an account manager" />
              </SelectTrigger>
              <SelectContent>
                {(accountManagers ?? []).map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#0B132B]">
              OpenServe dealer code
            </Label>
            <Input
              value={openserveCode}
              onChange={(e) => setOpenserveCode(e.target.value)}
              placeholder="Copied from the OpenServe portal"
              className="h-9 text-sm"
            />
          </div>
          {codeChanged ? (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#0B132B]">
                Confirm OpenServe dealer code
              </Label>
              <Input
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                placeholder="Re-enter the code to confirm"
                className="h-9 text-sm"
              />
              {showCodeMismatch ? (
                <p className="text-xs text-red-600">The codes do not match.</p>
              ) : null}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={updateAgent.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={updateAgent.isPending || !codeConfirmed}
            onClick={() => void handleSave()}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
          >
            {updateAgent.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Save assignment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Code-ONLY editor for operations / poc_support (and reused conceptually): set the
// OpenServe dealer code with double-entry confirmation. No assignment/activation —
// those stay admin/AM only. The code is the agent's ONE code; it is never generated.
function EditCodeDialog({
  agent,
  open,
  onOpenChange,
}: {
  agent: Agent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateAgent = useUpdateAgent();

  const [openserveCode, setOpenserveCode] = useState(
    agent.openserveDealerCode ?? "",
  );
  const [confirmCode, setConfirmCode] = useState("");

  useEffect(() => {
    if (open) {
      setOpenserveCode(agent.openserveDealerCode ?? "");
      setConfirmCode("");
    }
  }, [open, agent.openserveDealerCode]);

  const trimmedCode = openserveCode.trim();
  const originalCode = (agent.openserveDealerCode ?? "").trim();
  const codeChanged = trimmedCode !== originalCode;
  // Double-entry: a new/changed non-empty code must be typed identically twice.
  const codeNeedsConfirm = codeChanged && trimmedCode.length > 0;
  const codeConfirmed = !codeNeedsConfirm || confirmCode.trim() === trimmedCode;
  const showCodeMismatch =
    codeNeedsConfirm &&
    confirmCode.trim().length > 0 &&
    confirmCode.trim() !== trimmedCode;
  // Only enable Save when the code actually changed and (if needed) is confirmed.
  const canSave = codeChanged && codeConfirmed;

  async function handleSave() {
    if (!canSave) return;
    try {
      await updateAgent.mutateAsync({
        id: agent.id,
        data: { openserveDealerCode: trimmedCode },
      });
      await queryClient.invalidateQueries({
        queryKey: getGetAgentQueryKey(agent.id),
      });
      await queryClient.invalidateQueries({
        queryKey: getListAgentsQueryKey(),
      });
      toast({
        title: "Agent code updated",
        description: `${agent.name}'s OpenServe dealer code has been saved.`,
      });
      onOpenChange(false);
    } catch {
      toast({
        variant: "destructive",
        title: "Could not update the code",
        description: "Something went wrong. Please try again.",
      });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!updateAgent.isPending) onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit OpenServe dealer code</DialogTitle>
          <DialogDescription>
            Set the OpenServe dealer code for {agent.name}. Enter it twice to
            confirm — this is the agent&apos;s only code and is never generated.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#0B132B]">
              OpenServe dealer code
            </Label>
            <Input
              value={openserveCode}
              onChange={(e) => setOpenserveCode(e.target.value)}
              placeholder="Copied from the OpenServe portal"
              className="h-9 text-sm"
            />
          </div>
          {codeChanged ? (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#0B132B]">
                Confirm OpenServe dealer code
              </Label>
              <Input
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                placeholder="Re-enter the code to confirm"
                className="h-9 text-sm"
              />
              {showCodeMismatch ? (
                <p className="text-xs text-red-600">The codes do not match.</p>
              ) : null}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={updateAgent.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={updateAgent.isPending || !canSave}
            onClick={() => void handleSave()}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
          >
            {updateAgent.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Save code"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BackLink() {
  return (
    <Link href="/agents">
      <Button
        variant="ghost"
        className="text-gray-500 hover:text-[#0B132B] -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to agents
      </Button>
    </Link>
  );
}

function StatCard({
  label,
  value,
  accent = "#0B132B",
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <Card className="border-gray-100 shadow-sm">
      <CardContent className="p-4">
        <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
        <p className="text-2xl font-bold" style={{ color: accent }}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  tone = "default",
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  tone?: "default" | "warning";
}) {
  const warning = tone === "warning";
  return (
    <div className="flex items-start gap-3">
      <Icon
        className={`w-4 h-4 mt-1 shrink-0 ${
          warning ? "text-[#B45309]" : "text-gray-400"
        }`}
      />
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p
          className={`text-sm font-medium ${
            warning ? "text-[#B45309]" : "text-[#0B132B]"
          }`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function PlanStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-[#0B132B] mt-0.5">{value}</p>
    </div>
  );
}

function ComplianceTab({
  agentId,
  onboardingId,
  ficaDocuments,
}: {
  agentId: number;
  onboardingId?: number | null;
  ficaDocuments?: AgentFicaDocument[];
}) {
  const { data, isLoading, error } = useGetAgentCompliance(agentId);

  if (isLoading)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-gray-400">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading compliance details…
        </CardContent>
      </Card>
    );
  if (error || !data)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-red-500">
          Could not load compliance details. Refresh the page to try again.
        </CardContent>
      </Card>
    );

  if (!data.hasOnboarding)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-gray-400">
          No onboarding application is linked to this agent, so there is no KYC
          or agreement record to show.
        </CardContent>
      </Card>
    );

  const addressParts = [
    data.addressLine,
    data.city,
    data.province,
    data.postalCode,
  ].filter((p): p is string => !!p && p.trim().length > 0);
  const address = addressParts.length > 0 ? addressParts.join(", ") : "—";

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            KYC / FICA details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <PlanStat label="Full name" value={data.fullName || "—"} />
            <PlanStat label="ID number" value={data.idNumberMasked || "—"} />
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">
                Application status
              </p>
              {data.onboardingStatus ? (
                <StatusBadge status={data.onboardingStatus} />
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </div>
            <PlanStat
              label="Reviewed"
              value={data.reviewedAt ? formatDate(data.reviewedAt) : "—"}
            />
          </div>
          <DetailRow
            icon={MapPin}
            label="Residential address"
            value={address}
          />
          {data.reviewerNotes ? (
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">
                Reviewer notes
              </p>
              <p className="text-sm text-[#0B132B]">{data.reviewerNotes}</p>
            </div>
          ) : null}
          {onboardingId && ficaDocuments && ficaDocuments.length > 0 ? (
            <div>
              <p className="text-xs text-gray-500 font-medium mb-2">
                Verification documents
              </p>
              <div className="space-y-2">
                {ficaDocuments.map((doc) => (
                  <AuthenticatedDocumentLink
                    key={doc.documentKey}
                    apiPath={`/api/onboarding-reviews/${onboardingId}/documents/${doc.documentKey}`}
                    label={doc.label}
                    variant="card"
                  />
                ))}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-lg text-[#0B132B]">
            Field Sales Agent Agreement
          </CardTitle>
          {data.msaRequired ? (
            <span
              className={
                data.msaFullySigned
                  ? "rounded-full bg-[#00A859]/10 px-3 py-1 text-xs font-semibold text-[#00A859]"
                  : "rounded-full bg-[#FFF7ED] px-3 py-1 text-xs font-semibold text-[#B45309]"
              }
            >
              {data.msaFullySigned ? "Fully signed" : "Awaiting signatures"}
            </span>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {data.msaRequired ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">
                    Agent signature
                  </p>
                  {data.msaAgentSignedAt ? (
                    <p className="text-sm text-[#0B132B]">
                      {data.msaAgentSignedName || "Signed"} ·{" "}
                      {formatDate(data.msaAgentSignedAt)}
                    </p>
                  ) : (
                    <p className="text-sm text-[#B45309]">Awaiting agent</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">
                    Mooya counter-signature
                  </p>
                  {data.msaAdminSignedAt ? (
                    <p className="text-sm text-[#0B132B]">
                      {data.msaAdminSignedName || "Signed"} ·{" "}
                      {formatDate(data.msaAdminSignedAt)}
                    </p>
                  ) : (
                    <p className="text-sm text-[#B45309]">
                      Awaiting Mooya admin
                    </p>
                  )}
                </div>
              </div>
              {data.msaDocumentAvailable && data.onboardingId ? (
                <a
                  href={`/api/onboarding-reviews/${data.onboardingId}/msa/document`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-gray-100 px-3 py-2 text-sm font-medium text-[#0B132B] transition-colors hover:border-[#E6007E] hover:text-[#E6007E]"
                >
                  <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                  View signed agreement
                </a>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-gray-400">
              No agent agreement is required for this agent type.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Staff-facing MSA surface: view the rendered Field Sales Agent Agreement, send
// it to the agent by email, and download the executed PDF. Gated to the
// compliance-view roles + field_sales by the caller.
function AgreementTab({ onboardingId }: { onboardingId: number }) {
  const { toast } = useToast();
  const { data, isLoading, error } = useGetOnboardingMsa(onboardingId);
  const sendMsa = useSendOnboardingMsa();

  if (isLoading)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-gray-400">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading agreement…
        </CardContent>
      </Card>
    );
  if (error || !data)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-red-500">
          Could not load the agreement. Refresh the page to try again.
        </CardContent>
      </Card>
    );
  if (!data.required)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-gray-400">
          No agent agreement is required for this agent type.
        </CardContent>
      </Card>
    );

  const handleSend = () => {
    sendMsa.mutate(
      { id: onboardingId },
      {
        onSuccess: (res) =>
          toast({
            title: "Agreement sent",
            description: `Emailed to ${res.recipientEmail}${
              res.documentAttached ? " with the signed PDF attached" : ""
            }.`,
          }),
        onError: () =>
          toast({
            title: "Could not send agreement",
            description:
              "The agreement email failed to send. Please try again.",
            variant: "destructive",
          }),
      },
    );
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg text-[#0B132B]">
            Field Sales Agent Agreement
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handleSend}
              disabled={sendMsa.isPending}
              className="bg-[#E6007E] text-white hover:bg-[#c4006b]"
            >
              {sendMsa.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              <span className="ml-2">Send to agent</span>
            </Button>
            {data.documentAvailable ? (
              <a
                href={`/api/onboarding-reviews/${onboardingId}/msa/document`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-[#0B132B] transition-colors hover:border-[#E6007E] hover:text-[#E6007E]"
              >
                <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                Download PDF
              </a>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="max-h-[70vh] overflow-y-auto">
          <MsaContractDocument
            contract={data.contract}
            prefill={data.prefill}
            agentSignedName={data.agentSignedName}
            agentSignedAt={data.agentSignedAt}
            adminSignedName={data.adminSignedName}
            adminSignedAt={data.adminSignedAt}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function TrainingTab({ agentId }: { agentId: number }) {
  const { data, isLoading, error } = useGetAgentTraining(agentId);

  if (isLoading)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-gray-400">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading training status…
        </CardContent>
      </Card>
    );
  if (error || !data)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-red-500">
          Could not load training status. Refresh the page to try again.
        </CardContent>
      </Card>
    );

  const attempt = data.latestAttempt ?? null;
  const status: "certified" | "in_progress" | "not_started" | "no_account" =
    !data.hasUser
      ? "no_account"
      : data.completed
        ? "certified"
        : data.currentSlide > 0 || attempt
          ? "in_progress"
          : "not_started";
  const statusLabel = {
    certified: "Certified",
    in_progress: "In progress",
    not_started: "Not started",
    no_account: "No account linked",
  }[status];
  const badgeClass =
    status === "certified"
      ? "rounded-full bg-[#00A859]/10 px-3 py-1 text-xs font-semibold text-[#00A859]"
      : status === "in_progress"
        ? "rounded-full bg-[#FFF7ED] px-3 py-1 text-xs font-semibold text-[#B45309]"
        : "rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500";

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-lg text-[#0B132B]">
          Training &amp; certification
        </CardTitle>
        <span className={badgeClass}>{statusLabel}</span>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data.hasUser ? (
          <p className="text-sm text-gray-400">
            No portal account is linked to this agent yet, so there is no
            training record to show.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <PlanStat
                label="Slides completed"
                value={`${Math.min(data.currentSlide, data.totalSlides)} / ${data.totalSlides}`}
              />
              <PlanStat
                label="Exam result"
                value={attempt ? `${attempt.score} / ${attempt.totalQuestions}` : "—"}
              />
              <PlanStat
                label="Pass mark"
                value={attempt ? `${attempt.passMark} / ${attempt.totalQuestions}` : "—"}
              />
              <PlanStat
                label="Last attempt"
                value={attempt ? formatDate(attempt.completedAt) : "—"}
              />
            </div>
            {attempt ? (
              <div>
                <p className="text-xs text-gray-500 font-medium mb-1">
                  Latest exam outcome
                </p>
                <span
                  className={
                    attempt.passed
                      ? "rounded-full bg-[#00A859]/10 px-3 py-1 text-xs font-semibold text-[#00A859]"
                      : "rounded-full bg-[#FEF2F2] px-3 py-1 text-xs font-semibold text-[#B91C1C]"
                  }
                >
                  {attempt.passed ? "Passed" : "Did not pass"}
                </span>
              </div>
            ) : (
              <p className="text-sm text-gray-400">
                No exam attempts recorded yet.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BankTab({ agentId }: { agentId: number }) {
  // The agent record is the source of truth for the account on file: approval
  // mirrors bank-change details onto it, AND onboarding-captured bank lives
  // there too (bank-change requests only exist for later changes). The Agent
  // payload carries bank fields for admin only, and this tab is admin-only.
  // useGetAgent shares the parent's query cache, so this is not a second fetch.
  const { data: agent } = useGetAgent(agentId);
  const { data, isLoading, error } = useListBankChangeRequests();
  const requests = (data ?? [])
    .filter((r) => r.agentId === agentId)
    .sort(
      (a, b) =>
        new Date(b.submittedAt ?? 0).getTime() -
        new Date(a.submittedAt ?? 0).getTime(),
    );
  const hasBankOnFile = !!(agent?.bankName || agent?.accountNumberMasked);

  if (isLoading)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-gray-400">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading bank details…
        </CardContent>
      </Card>
    );
  if (error)
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 text-sm text-red-500">
          Could not load bank details. Refresh the page to try again.
        </CardContent>
      </Card>
    );

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            Bank account on file
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasBankOnFile ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <PlanStat label="Bank" value={agent?.bankName || "—"} />
              <PlanStat
                label="Account"
                value={agent?.accountNumberMasked || "—"}
              />
              <PlanStat
                label="Branch code"
                value={agent?.branchCode || "—"}
              />
              <PlanStat
                label="Account type"
                value={
                  agent?.accountType
                    ? (ACCOUNT_TYPE_LABELS[agent.accountType] ??
                      agent.accountType)
                    : "—"
                }
              />
            </div>
          ) : (
            <p className="text-sm text-gray-400">
              No verified bank account on file for this agent yet.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-lg text-[#0B132B]">
            Bank change requests
          </CardTitle>
          <Link
            href="/bank-changes"
            className="text-sm font-medium text-[#E6007E] hover:underline"
          >
            Review queue
          </Link>
        </CardHeader>
        <CardContent className="space-y-3">
          {requests.length > 0 ? (
            requests.map((req) => (
              <div
                key={req.id}
                className="rounded-md border border-gray-100 p-3 space-y-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-[#0B132B]">
                    {req.newBankName}
                  </span>
                  <StatusBadge status={req.status} />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span className="tabular-nums">
                    {req.newAccountNumberMasked}
                  </span>
                  {req.newBranchCode ? (
                    <span>Branch {req.newBranchCode}</span>
                  ) : null}
                  {req.accountType ? (
                    <span>
                      {ACCOUNT_TYPE_LABELS[req.accountType] ?? req.accountType}
                    </span>
                  ) : null}
                  <span>{formatDate(req.submittedAt)}</span>
                </div>
                {(req.status === "rejected" ||
                  req.status === "action_required") &&
                req.reviewerNotes ? (
                  <p
                    className={
                      req.status === "rejected"
                        ? "text-xs text-red-700"
                        : "text-xs text-amber-700"
                    }
                  >
                    {req.reviewerNotes}
                  </p>
                ) : null}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400">
              No bank change requests for this agent.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActivityTab({ agentId, role }: { agentId: number; role: string }) {
  const canEarnings = role === "admin";
  const canTickets = role === "account_manager" || role === "admin";
  const canDisputes = role === "admin";
  // The audit log is admin-only on the server (/audit-logs is admin-gated), so
  // only surface the section to admins — other roles would just get a 403.
  const canAudit = role === "admin";
  // Notification history is admin-only on the server (/agents/:id/notifications),
  // so mirror that gate here — other roles would just get a 403.
  const canNotifications = role === "admin";
  return (
    <div className="space-y-6">
      <OrdersSection agentId={agentId} />
      {canEarnings ? <EarningsSection agentId={agentId} /> : null}
      {canTickets ? <TicketsSection agentId={agentId} /> : null}
      {canDisputes ? <DisputesSection agentId={agentId} /> : null}
      {canNotifications ? <NotificationsSection agentId={agentId} /> : null}
      {canAudit ? <AuditSection agentId={agentId} /> : null}
    </div>
  );
}

function ActivitySection({
  title,
  isLoading,
  error,
  isEmpty,
  emptyText,
  note,
  children,
}: {
  title: string;
  isLoading: boolean;
  error: boolean;
  isEmpty: boolean;
  emptyText: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg text-[#0B132B]">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <p className="text-sm text-gray-400">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
            Loading…
          </p>
        ) : error ? (
          <p className="text-sm text-red-500">
            Could not load. Refresh the page to try again.
          </p>
        ) : isEmpty ? (
          <p className="text-sm text-gray-400">{emptyText}</p>
        ) : (
          <>
            {children}
            {note ? (
              <p className="pt-1 text-xs text-gray-400">{note}</p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityRow({
  title,
  subtitle,
  status,
  right,
}: {
  title: string;
  subtitle?: string;
  status?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-gray-100 px-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-[#0B132B]">{title}</p>
        {subtitle ? (
          <p className="truncate text-xs text-gray-500">{subtitle}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {right}
        {status ? <StatusBadge status={status} /> : null}
      </div>
    </div>
  );
}

function OrdersSection({ agentId }: { agentId: number }) {
  const { data, isLoading, error } = useListOrders({
    agentId,
    page: 1,
    pageSize: 5,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  return (
    <ActivitySection
      title="Recent orders"
      isLoading={isLoading}
      error={!!error}
      isEmpty={items.length === 0}
      emptyText="No orders for this agent yet."
      note={
        total > items.length
          ? `Showing ${items.length} of ${total} orders.`
          : undefined
      }
    >
      {items.map((o) => (
        <ActivityRow
          key={o.id}
          title={o.customerName}
          subtitle={`${o.reference} · ${formatDate(o.submittedAt)}`}
          status={o.status}
          right={
            <span className="text-sm font-medium text-[#0B132B] tabular-nums">
              {formatCents(o.priceCents)}
            </span>
          }
        />
      ))}
    </ActivitySection>
  );
}

function EarningsSection({ agentId }: { agentId: number }) {
  const { data, isLoading, error } = useListTransactions({
    agentId,
    page: 1,
    pageSize: 5,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  return (
    <ActivitySection
      title="Earnings"
      isLoading={isLoading}
      error={!!error}
      isEmpty={items.length === 0}
      emptyText="No earnings recorded for this agent yet."
      note={
        total > items.length
          ? `Showing ${items.length} of ${total} transactions.`
          : undefined
      }
    >
      {items.map((t) => (
        <ActivityRow
          key={t.id}
          title={TRANSACTION_TYPE_LABELS[t.type] ?? t.type}
          subtitle={`${t.reference} · ${formatDate(t.createdAt)}`}
          status={t.status}
          right={
            <span className="text-sm font-medium text-[#0B132B] tabular-nums">
              {formatCents(t.amountCents)}
            </span>
          }
        />
      ))}
    </ActivitySection>
  );
}

function TicketsSection({ agentId }: { agentId: number }) {
  const { data, isLoading, error } = useListTickets({
    agentId,
    page: 1,
    pageSize: 5,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  return (
    <ActivitySection
      title="Recent support tickets"
      isLoading={isLoading}
      error={!!error}
      isEmpty={items.length === 0}
      emptyText="No support tickets for this agent yet."
      note={
        total > items.length
          ? `Showing ${items.length} of ${total} tickets.`
          : undefined
      }
    >
      {items.map((t) => (
        <ActivityRow
          key={t.id}
          title={t.subject}
          subtitle={`${t.reference} · ${formatDate(t.createdAt)}`}
          status={t.status}
        />
      ))}
    </ActivitySection>
  );
}

function DisputesSection({ agentId }: { agentId: number }) {
  const { data, isLoading, error } = useListDisputes({
    agentId,
    page: 1,
    pageSize: 5,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  return (
    <ActivitySection
      title="Recent disputes"
      isLoading={isLoading}
      error={!!error}
      isEmpty={items.length === 0}
      emptyText="No disputes for this agent yet."
      note={
        total > items.length
          ? `Showing ${items.length} of ${total} disputes.`
          : undefined
      }
    >
      {items.map((d) => (
        <ActivityRow
          key={d.id}
          title={d.subject}
          subtitle={`${d.reference} · ${formatDate(d.createdAt)}`}
          status={d.status}
          right={
            d.amountCents != null ? (
              <span className="text-sm font-medium text-[#0B132B] tabular-nums">
                {formatCents(d.amountCents)}
              </span>
            ) : undefined
          }
        />
      ))}
    </ActivitySection>
  );
}

function AuditSection({ agentId }: { agentId: number }) {
  // Admin-only: who changed what on this agent record. Scoped server-side to
  // entityType "agent" + this agentId. Order/wallet events are not audited yet.
  const { data, isLoading, error } = useListAuditLogs({
    entityType: "agent",
    entityId: agentId,
    page: 1,
    pageSize: 5,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  return (
    <ActivitySection
      title="Activity log"
      isLoading={isLoading}
      error={!!error}
      isEmpty={items.length === 0}
      emptyText="No recorded changes to this agent yet."
      note={
        total > items.length
          ? `Showing ${items.length} of ${total} events.`
          : undefined
      }
    >
      {items.map((a) => (
        <ActivityRow
          key={a.id}
          title={a.detail || a.action}
          subtitle={`${a.actorName || "System"} · ${formatDate(a.createdAt)}`}
        />
      ))}
    </ActivitySection>
  );
}

function NotificationsSection({ agentId }: { agentId: number }) {
  // Admin-only: the in-app notifications this agent has received. Scoped
  // server-side to the agent's linked user account.
  const { data, isLoading, error } = useGetAgentNotifications(agentId);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasUser = data?.hasUser ?? true;
  return (
    <ActivitySection
      title="Notifications"
      isLoading={isLoading}
      error={!!error}
      isEmpty={items.length === 0}
      emptyText={
        hasUser
          ? "No notifications for this agent yet."
          : "No portal account is linked to this agent, so there is no notification history."
      }
      note={
        total > items.length
          ? `Showing ${items.length} of ${total} notifications.`
          : undefined
      }
    >
      {items.map((n) => (
        <ActivityRow
          key={n.id}
          title={n.title}
          subtitle={n.body}
          right={
            <div className="flex shrink-0 items-center gap-2">
              {!n.readAt ? (
                <span
                  className="h-2 w-2 rounded-full bg-[#E6007E]"
                  aria-label="Unread"
                />
              ) : null}
              <span className="text-xs text-gray-400 tabular-nums">
                {formatDate(n.createdAt)}
              </span>
            </div>
          }
        />
      ))}
    </ActivitySection>
  );
}

function CommissionPlanCard({
  agentId,
  currentPlanId,
}: {
  agentId: number;
  currentPlanId?: number | null;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const plansQuery = useListCommissionPlans();
  const setPlan = useSetAgentCommissionPlan();
  const [saving, setSaving] = useState(false);

  const plans = plansQuery.data?.items ?? [];
  const defaultPlan = plans.find((p) => p.isDefault);
  const currentPlan = plans.find((p) => p.id === currentPlanId) ?? defaultPlan;

  async function assign(value: string) {
    const commissionPlanId = value === "" ? null : Number(value);
    setSaving(true);
    try {
      await setPlan.mutateAsync({ id: agentId, data: { commissionPlanId } });
      await queryClient.invalidateQueries({
        queryKey: getGetAgentQueryKey(agentId),
      });
      await queryClient.invalidateQueries({
        queryKey: getListAgentsQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: getListCommissionPlansQueryKey(),
      });
      toast({ title: "Commission plan updated" });
    } catch {
      toast({
        variant: "destructive",
        title: "Could not update plan",
        description: "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg text-[#0B132B]">
          Commission plan
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-3">
          {plansQuery.error ? (
            <p className="text-sm text-red-500">
              Could not load commission plans. Refresh the page to try again.
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            <select
              aria-label="Commission plan"
              value={currentPlanId ?? ""}
              onChange={(e) => void assign(e.target.value)}
              disabled={
                saving || plansQuery.isLoading || Boolean(plansQuery.error)
              }
              className="text-sm border border-gray-200 rounded-md py-1.5 px-2 bg-white focus:outline-none focus:border-[#E6007E] disabled:opacity-60"
            >
              <option value="">
                Default{defaultPlan ? ` (${defaultPlan.name})` : ""}
              </option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {saving ? (
              <Loader2 size={15} className="animate-spin text-gray-400" />
            ) : null}
          </div>
          <p className="text-xs text-gray-500">
            Agents without an explicit plan fall back to the default
            {defaultPlan ? ` (${defaultPlan.name})` : ""}.
          </p>
        </div>

        {currentPlan ? (
          <div className="border-t border-gray-100 pt-4 space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[#0B132B]">
                {currentPlan.name}
              </h3>
              {currentPlanId == null ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-[#E6007E]/10 text-[#E6007E]">
                  Default
                </span>
              ) : null}
            </div>
            {currentPlan.description ? (
              <p className="text-xs text-gray-500">{currentPlan.description}</p>
            ) : null}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <PlanStat
                label="Free sales / month"
                value={String(currentPlan.thresholdFreeCount)}
              />
              <PlanStat
                label="Retainer"
                value={formatCents(currentPlan.retainerCents)}
              />
              <PlanStat
                label="Retainer KPI"
                value={`${currentPlan.retainerMinLeads} leads · ${currentPlan.retainerMinActivations} act.`}
              />
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">
                Once-off commission per package
              </p>
              {currentPlan.rates.length > 0 ? (
                <div className="rounded-md border border-gray-100 divide-y divide-gray-100">
                  {currentPlan.rates.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <span className="text-gray-700">{r.label}</span>
                      <span className="tabular-nums font-medium text-[#0B132B]">
                        {formatCents(r.commissionCents)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  No package rates configured.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
